import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarDays, Check, ChevronDown, MapPin } from "lucide-react";
import { useEnquiry } from "@/components/EnquiryModal";
import { tripTypeFor } from "@/lib/site";

export function DestinationCard({ dest }) {
  return (
    <Link to={`/destinations/${dest.slug}`} data-testid={`destination-card-${dest.slug}`}
          className="group relative block rounded-2xl overflow-hidden h-72 shadow-sm hover:shadow-xl transition-shadow duration-300">
      <img loading="lazy" src={dest.image} alt={`${dest.name} — ${dest.tagline}`}
           className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-navydeep/90 via-navydeep/25 to-transparent" />
      <div className="absolute bottom-0 p-5">
        <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-sky-300">{dest.country}</p>
        <h3 className="font-heading text-2xl font-extrabold text-white tracking-tight">{dest.name}</h3>
        <p className="font-display italic text-sm text-slate-300">{dest.tagline}</p>
      </div>
    </Link>
  );
}

export function PackageCard({ pkg }) {
  const { openEnquiry } = useEnquiry();
  return (
    <div data-testid={`package-card-${pkg.slug}`}
         className="group bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300 flex flex-col">
      <Link to={`/packages/${pkg.slug}`} className="relative block h-56 overflow-hidden">
        <img loading="lazy" src={pkg.image} alt={`${pkg.title} — ${pkg.destination}`}
             className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <span className="absolute top-3 left-3 rounded-full bg-navy/85 backdrop-blur px-3 py-1 text-xs font-bold text-white">
          {pkg.nights}N / {pkg.days}D
        </span>
        <span className="absolute top-3 right-3 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-[11px] font-bold tracking-wide uppercase text-royal">
          {(pkg.holiday_types || [])[0]}
        </span>
      </Link>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-skybrand">{pkg.destination} · {pkg.region}</p>
        <Link to={`/packages/${pkg.slug}`}>
          <h3 className="mt-1 font-heading text-lg font-bold text-slate-900 tracking-tight group-hover:text-royal transition-colors">{pkg.title}</h3>
        </Link>
        <ul className="mt-3 space-y-1.5">
          {(pkg.highlights || []).slice(0, 3).map((h) => (
            <li key={h} className="flex items-start gap-2 text-sm text-slate-600">
              <Check className="w-4 h-4 text-skybrand shrink-0 mt-0.5" /> {h}
            </li>
          ))}
        </ul>
        {pkg.price_from ? (
          <div className="mt-3 flex items-end justify-between gap-2">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Starting from</p>
              <p className="font-heading text-xl font-extrabold text-navy">₹{Number(pkg.price_from).toLocaleString("en-IN")}</p>
              <p className="text-[10px] text-slate-400">{pkg.price_basis || "per person"}{pkg.flights_included ? " · flights incl." : ""}</p>
            </div>
            <p className="text-[10px] text-slate-400 text-right leading-tight">*Tentative rate<br />final quote on request</p>
          </div>
        ) : (
          <p className="mt-3 text-[11px] text-slate-400">Costing on request — customised to your dates, hotels & travellers.</p>
        )}
        <div className="mt-auto pt-4 flex gap-2">
          <Link to={`/packages/${pkg.slug}`} data-testid={`package-itinerary-button-${pkg.slug}`}
                className="flex-1 rounded-full border border-navy px-4 py-2.5 text-center text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors">
            View Details
          </Link>
          <button onClick={() => openEnquiry({ packageName: pkg.title, destination: pkg.destination, tripType: tripTypeFor(pkg) })}
                  data-testid={`package-quote-button-${pkg.slug}`}
                  className="flex-1 rounded-full bg-navy px-4 py-2.5 text-sm font-semibold text-white hover:bg-royal transition-colors">
            Get a Quote
          </button>
        </div>
      </div>
    </div>
  );
}

export function VisaCard({ visa }) {
  const short = visa.slug.replace(/-visa$/, "");
  return (
    <Link to={`/visa/${visa.slug}`} data-testid={`visa-card-${short}`}
          className="group bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm hover:shadow-xl hover:border-sky-200 transition-all duration-300 flex flex-col">
      <span className="w-11 h-11 rounded-xl bg-sky-50 flex items-center justify-center">
        <MapPin className="w-5 h-5 text-skybrand" />
      </span>
      <h3 className="mt-4 font-heading text-lg font-bold text-slate-900 tracking-tight group-hover:text-royal transition-colors">{visa.country} Visa</h3>
      <p className="mt-2 text-sm text-slate-500 leading-relaxed line-clamp-3">{visa.overview}</p>
      <span className="mt-4 text-sm font-semibold text-royal">View requirements →</span>
    </Link>
  );
}

export function GroupTourCard({ tour }) {
  const [open, setOpen] = useState(false);
  const { openEnquiry } = useEnquiry();
  return (
    <div data-testid={`group-tour-card-${tour.slug}`}
         className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300">
      <div className="relative h-52">
        <img loading="lazy" src={tour.image} alt={tour.name} className="w-full h-full object-cover" />
        <span className="absolute top-3 left-3 rounded-full bg-gold px-3 py-1 text-[11px] font-bold tracking-wide uppercase text-navydeep">Fixed Departure</span>
        <span className="absolute bottom-3 left-3 rounded-full bg-navy/85 backdrop-blur px-3 py-1 text-xs font-bold text-white flex items-center gap-1.5">
          <CalendarDays className="w-3.5 h-3.5" /> {tour.duration}
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-heading text-lg font-bold text-slate-900 tracking-tight">{tour.name}</h3>
        <p className="mt-1 text-sm text-slate-500">{tour.places}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {(tour.departures || []).map((d) => (
            <span key={d} className="rounded-full bg-sky-50 border border-sky-100 px-3 py-1 text-xs font-semibold text-royal">{d}</span>
          ))}
        </div>
        {tour.price_from ? (
          <p className="mt-3 text-sm text-slate-500">
            Starting from <span className="font-heading text-lg font-extrabold text-navy">₹{Number(tour.price_from).toLocaleString("en-IN")}</span> {tour.price_basis || "per person"}
            <span className="block text-[10px] text-slate-400">*Tentative rate — final quote on request</span>
          </p>
        ) : null}
        <div className="mt-4 flex gap-2">
          <button onClick={() => setOpen(!open)} data-testid={`group-tour-itinerary-${tour.slug}`}
                  className="flex-1 flex items-center justify-center gap-1 rounded-full border border-navy px-4 py-2.5 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors">
            View Itinerary <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          <button onClick={() => openEnquiry({ packageName: tour.name, tripType: "Group Tour", destination: tour.places })}
                  data-testid={`group-tour-quote-${tour.slug}`}
                  className="flex-1 rounded-full bg-navy px-4 py-2.5 text-sm font-semibold text-white hover:bg-royal transition-colors">
            Get a Quote
          </button>
        </div>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }} className="overflow-hidden">
              <div className="pt-4 mt-4 border-t border-slate-100">
                <p className="text-sm text-slate-600 leading-relaxed">{tour.description}</p>
                <ul className="mt-3 space-y-1.5">
                  {(tour.highlights || []).map((h) => (
                    <li key={h} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-skybrand shrink-0 mt-0.5" /> {h}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export function OfferCard({ offer, packages }) {
  const { openEnquiry } = useEnquiry();
  const pkg = (packages || []).find((p) => p.slug === offer.package_slug);
  const image = offer.image || pkg?.image;
  const dest = offer.destination || pkg?.destination || "";
  const save = (offer.was_price || 0) - (offer.now_price || 0);
  return (
    <div data-testid={`offer-card-${offer.id}`}
         className="group relative bg-white rounded-2xl border-2 border-amber-200 overflow-hidden shadow-sm hover:shadow-xl hover:border-gold transition-all duration-300 flex flex-col">
      {image && (
        <div className="relative h-44 overflow-hidden">
          <img loading="lazy" src={image} alt={offer.title}
               className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
          {save > 0 && (
            <span className="absolute top-3 left-3 rounded-full bg-red-600 px-3 py-1 text-xs font-extrabold tracking-wide text-white shadow-lg">
              SAVE ₹{save.toLocaleString("en-IN")}
            </span>
          )}
          {offer.tag && (
            <span className="absolute top-3 right-3 rounded-full bg-gold px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-navydeep shadow-lg">
              {offer.tag}
            </span>
          )}
        </div>
      )}
      <div className="p-5 flex flex-col flex-1">
        {dest && <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-skybrand">{dest}</p>}
        <h3 className="mt-1 font-heading text-lg font-bold text-slate-900 tracking-tight">{offer.title}</h3>
        {offer.description && <p className="mt-1.5 text-sm text-slate-500 leading-relaxed line-clamp-2">{offer.description}</p>}
        <div className="mt-3 flex items-end gap-2.5">
          {offer.was_price ? <span className="text-sm text-slate-400 line-through mb-1">₹{Number(offer.was_price).toLocaleString("en-IN")}</span> : null}
          <span className="font-heading text-2xl font-extrabold text-navy">₹{Number(offer.now_price || 0).toLocaleString("en-IN")}</span>
          <span className="text-xs text-slate-400 mb-1">per person*</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-slate-500">
          {offer.valid_till && <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-red-500" /> Valid till {offer.valid_till}</span>}
          {offer.departure_note && <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-skybrand" /> {offer.departure_note}</span>}
        </div>
        <div className="mt-auto pt-4 flex gap-2">
          {pkg && (
            <Link to={`/packages/${pkg.slug}`} data-testid={`offer-view-${offer.id}`}
                  className="flex-1 rounded-full border border-navy px-4 py-2.5 text-center text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors">
              View Package
            </Link>
          )}
          <button onClick={() => openEnquiry({ packageName: `OFFER: ${offer.title}`, destination: dest, tripType: pkg ? tripTypeFor(pkg) : "Other" })}
                  data-testid={`offer-grab-${offer.id}`}
                  className="flex-1 rounded-full bg-gold px-4 py-2.5 text-sm font-bold text-navydeep hover:bg-amber-400 transition-colors">
            Grab This Offer
          </button>
        </div>
      </div>
    </div>
  );
}
